import {
  idsEqual,
  levelToRole,
  type HierarchyNode,
} from '@/features/hierarchy/hierarchy'
import type { ReportAccount } from './types'

function countMembers(nodes: HierarchyNode[], rootId: string): number {
  let count = 0
  for (const node of nodes) {
    if (node.level === 6 && node.id !== rootId) count += 1
  }
  return count
}

function buildPath(
  byId: Map<string, HierarchyNode>,
  id: string,
  rootId: string,
): string[] {
  const path: string[] = []
  const seen = new Set<string>()
  let cursor: string | null = id
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    if (!idsEqual(cursor, rootId)) path.unshift(cursor)
    const node = byId.get(cursor)
    if (!node?.parentId) break
    cursor = node.parentId
  }
  return path
}

export function nodeToReportAccount(
  node: HierarchyNode,
  nodes: HierarchyNode[],
  rootId: string,
): ReportAccount {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  return {
    id: node.id,
    username: node.username ?? node.name,
    displayName: node.name,
    role: levelToRole(node.level),
    parentId: node.parentId,
    path: buildPath(byId, node.id, rootId),
    memberCount: node.level === 6 ? 0 : countMembers(nodes.filter((n) => isDescendant(n, node.id, byId)), node.id),
  }
}

function isDescendant(
  node: HierarchyNode,
  ancestorId: string,
  byId: Map<string, HierarchyNode>,
): boolean {
  if (idsEqual(node.id, ancestorId)) return true
  let cursor = node.parentId
  const seen = new Set<string>()
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    if (idsEqual(cursor, ancestorId)) return true
    cursor = byId.get(cursor)?.parentId ?? null
  }
  return false
}

export function reportAccountsFromTree(
  nodes: HierarchyNode[],
  root: HierarchyNode,
): Map<string, ReportAccount> {
  const all = [root, ...nodes.filter((n) => !idsEqual(n.id, root.id))]
  const map = new Map<string, ReportAccount>()
  for (const node of all) {
    map.set(node.id, nodeToReportAccount(node, all, root.id))
  }
  return map
}

export function isBeneathRoot(
  nodes: HierarchyNode[],
  rootId: string,
  targetId: string,
): boolean {
  if (idsEqual(targetId, rootId)) return true
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const target = byId.get(targetId)
  if (!target) return false
  return isDescendant(target, rootId, byId) || idsEqual(target.parentId, rootId)
}
